/**
 * AuditPage — live data only. /api/v1/dashboard/audit.
 *
 * Two columns the production schema HAS but DOESN'T POPULATE:
 *   • transaction_history.tax       → 0% populated. GST shown is computed
 *     from gross (× 18/118 inclusive split).
 *   • earnings.tds_amount           → 0% populated. TDS shown is computed
 *     at 1% of creator earnings (Section 194-O standard for digital
 *     platforms).
 * Both numbers are surfaced with an explicit "computed, not collected" note
 * in their tooltips so finance/CFO can't accidentally read them as
 * collected-and-remitted.
 *
 * Removed: "Suspensions this month" — `suspended_users` table has 0 rows
 * lifetime AND `users.is_suspended = true` is 0 across roles 2/3. Replaced
 * with "Rejected creators" (the only audit-relevant signal currently exposed).
 */
import React from "react";
import { tokensV2 as T } from "./tokens.v2";
import { PageGuide } from "./PageGuide.v2";
import { Tile } from "./Tile.v2";
import { AreaChart } from "./AreaChart.v2";
import { Donut } from "./Donut.v2";
import { useDashboardPage, formatINR, formatNum, formatPct } from "./useDashboardPage.v2";
import { rangeWordFor, granLabel as glab } from "./rangeWord.v2";
import { RefundsQueueCard } from "./InsightsCards.v2";
import { LoadingShell, EmptyError } from "./PageState.v2";

export function AuditPage({ range, rangeId }) {
  const { data, loading, error, reload } = useDashboardPage("audit", range, rangeId);
  if (loading) return <LoadingShell tiles={4} />;
  if (!data) return <EmptyError title="Couldn't load Audit & Tax" error={error} onRetry={reload} />;

  const h = data.hero || {};
  const tax = data.taxBreakdown || {};
  const isTillDate = data.range?.isTillDate;
  const word = rangeWordFor(rangeId);
  const windowWord = word; // alias

  // Net commission % over gross (window). Useful for CFO tracking platform take rate.
  const takeRate = tax.gross > 0 ? (tax.platformCommission / tax.gross) * 100 : 0;

  const heroTiles = [
    {
      label: "GST liability", icon: "G", variant: "muted-lime",
      value: formatINR(h.gstLiability?.value),
      subValue: `${formatPct(18, 0)} of ₹${formatNum(h.gstLiability?.gross)} gross · ${windowWord}`,
      title:
        `GST collected on platform transactions (computed).\n` +
        `  Gross (${windowWord}):  ${formatINR(h.gstLiability?.gross)}\n` +
        `  GST @ 18% inclusive:  ${formatINR(h.gstLiability?.value)}\n` +
        `  Net of GST:           ${formatINR(tax.netRevenue)}\n` +
        `\n` +
        `${h.gstLiability?.note || "Tax column not populated by the platform; this is computed from gross using the 18% inclusive split (₹100 gross = ₹84.75 net + ₹15.25 GST)."}`,
    },
    {
      label: "TDS liability", icon: "T", variant: "muted-violet",
      value: formatINR(h.tdsLiability?.value),
      subValue: `${formatPct(1, 0)} of ₹${formatNum(h.tdsLiability?.earningsGross)} creator earnings · ${windowWord}`,
      title:
        `TDS withheld from creator payouts (computed).\n` +
        `  Creator earnings:  ${formatINR(h.tdsLiability?.earningsGross)}\n` +
        `  TDS @ 1%:          ${formatINR(h.tdsLiability?.value)}\n` +
        `\n` +
        `${h.tdsLiability?.note || "tds_amount column not populated; this is computed at 1% of creator earnings (Section 194-O standard for digital platforms)."}`,
    },
    {
      label: `Refunds approved · ${windowWord}`, icon: "R", variant: "muted-magenta",
      value: formatNum(h.refundsApproved?.count),
      subValue: `${formatINR(h.refundsApproved?.amount)} approved`,
      title:
        `Refund tickets approved by ops within the selected window.\n` +
        `  Tickets:  ${formatNum(h.refundsApproved?.count)}\n` +
        `  Amount:   ${formatINR(h.refundsApproved?.amount)}\n` +
        `\n` +
        `Approved tickets are net of rejections — fan got their money back.\n` +
        `Compare with rejected count below to monitor approval discipline.`,
    },
    {
      label: "Pending refunds · live", icon: "Q", variant: "muted-orange",
      value: formatNum(h.refundsPending?.count),
      subValue: h.refundsPending?.count > 0
        ? `${formatINR(h.refundsPending?.amount)} · oldest ${formatNum(h.refundsPending?.oldestDays)}d`
        : "queue clear",
      title:
        `Live refund queue — no date filter. This is what ops actually has\n` +
        `to process right now.\n` +
        `  Pending tickets:    ${formatNum(h.refundsPending?.count)}\n` +
        `  Pending amount:     ${formatINR(h.refundsPending?.amount)}\n` +
        `  Oldest age (days):  ${formatNum(h.refundsPending?.oldestDays)}\n` +
        `\n` +
        `If oldest age is large the queue is stale — escalate to ops lead.`,
    },
  ];

  // Tax breakdown table — finance-grade view
  const breakdown = [
    { label: "Gross transaction value", value: tax.gross },
    { label: "GST liability (18% inclusive)", value: tax.gstLiability },
    { label: "Net call revenue (after GST)", value: tax.netRevenue },
    { label: "Creator earnings (gross)", value: tax.earningsGross },
    { label: "TDS liability (1% of earnings)", value: tax.tdsLiability },
    { label: "Platform commission", value: tax.platformCommission },
    { label: "Payouts disbursed (creator side)", value: tax.payoutsDisbursed },
  ];

  // Donut prefers the lifetime mix when the window mix is tiny — a 5-ticket
  // donut isn't actionable, but the lifetime 215/83/5 split is.
  const windowMix = data.refundStatusWindow || [];
  const lifetimeMix = data.refundStatusLifetime || [];
  const useLifetimeMix = windowMix.reduce((s, r) => s + r.value, 0) < 20;
  const mix = (useLifetimeMix ? lifetimeMix : windowMix).map(r => ({
    label: r.label,
    value: r.value,
  }));
  const mixTotal = mix.reduce((s, r) => s + r.value, 0);
  const mixSubtitle = useLifetimeMix
    ? `lifetime — ${word} has too few tickets to chart`
    : `${word} · approved / rejected / pending`;

  const trend = data.trend?.monthlyApprovedRefunds || [];

  return (
    <>
      <div style={{ marginBottom: 16, padding: "0 4px" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.text.primary }}>Audit & Tax</div>
        <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4 }}>Refund operations · GST/TDS liability · creator payout discipline · live from production</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {heroTiles.map(t => <Tile key={t.label} {...t} />)}
      </div>

      {/* Refunds queue age — exposes SLA breaches on approved-but-unpaid refunds */}
      <div style={{ marginBottom: 12 }}>
        <RefundsQueueCard />
      </div>

      <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>
        Tax breakdown · {windowWord}
        {tax.gross > 0 && (
          <span style={{ fontWeight: 500, color: T.text.muted, marginLeft: 8 }}>
            · platform take rate {formatPct(takeRate, 1)}
          </span>
        )}
      </div>
      <div style={{ background: T.surface.card, border: `1px solid ${T.border.subtle}`, borderRadius: 12, padding: "14px 16px", marginBottom: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "8px 24px", fontSize: 13, fontFeatureSettings: T.font.nums, fontVariantNumeric: "tabular-nums" }}>
          {breakdown.map(row => (
            <React.Fragment key={row.label}>
              <div style={{ color: T.text.secondary }}>{row.label}</div>
              <div style={{ color: T.text.primary, fontWeight: 600, textAlign: "right" }}>{formatINR(row.value)}</div>
            </React.Fragment>
          ))}
        </div>
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border.subtle}`, fontSize: 11, color: T.text.muted, lineHeight: 1.5 }}>
          GST and TDS are <strong>computed from gross</strong> using statutory rates — the dedicated tax/TDS columns in the database are unpopulated. Treat as platform-side liability estimates, not as collected-and-remitted figures.
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 14, marginBottom: 12, alignItems: "stretch" }}>
        {mix.length > 0 && (
          <Donut title="Refund status mix" subtitle={mixSubtitle} data={mix} format={formatNum} centerLabel="Tickets" centerValue={formatNum(mixTotal)} />
        )}
        {trend.length > 0 && (() => {
          const gran = data.range?.granularity || 'month';
          const granLabel = glab(gran);
          const titleSuffix = isTillDate ? 'last 12 months' : word;
          return (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6, padding: "0 4px" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>Approved refunds — {titleSuffix}</div>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, padding: "4px 10px", background: T.surface.card, border: "1px solid " + T.border.subtle, borderRadius: 999 }}>{granLabel}</span>
              </div>
              <AreaChart data={trend} format={formatNum} summaryLabel={trend[trend.length - 1]?.label} summaryValue={formatNum(trend[trend.length - 1]?.value)} summaryDelta="" />
            </div>
          );
        })()}
      </div>

      {data.rejectedCreators >= 0 && (
        <div style={{ padding: "12px 14px", background: T.surface.card, border: `1px solid ${T.border.subtle}`, borderRadius: 12, fontSize: 13, color: T.text.secondary }}>
          <strong>Rejected creators:</strong> {formatNum(data.rejectedCreators)} accounts (users.is_rejected = true).
          <span style={{ color: T.text.muted, marginLeft: 6 }}>
            suspended_users table is empty in production and users.is_suspended is unused, so this is the only audit-grade signal currently exposed.
          </span>
        </div>
      )}
      <PageGuide groups={AUDIT_GUIDE} />
    </>
  );
}

// Definitions for every Audit & Tax card. GST and TDS are DERIVED/ESTIMATED —
// the tax/tds columns are not populated by the platform; everything else is read
// straight from the data.
const AUDIT_GUIDE = [
  {
    section: "Tax (estimated)",
    rows: [
      { card: "GST liability", means: "Estimated GST owed (18% inclusive of gross revenue).", rule: "DERIVED: transaction_history gross × 18/118. The tax column is not populated, so this is computed, not collected." },
      { card: "TDS liability", means: "Estimated 1% TDS on creator earnings (Section 194-O).", rule: "DERIVED: earnings gross × 1%. The tds_amount column is not populated." },
      { card: "Net call revenue (after GST)", means: "Call revenue after removing the estimated GST.", rule: "call-revenue gross − GST (derived)." },
    ],
  },
  {
    section: "Recorded figures",
    rows: [
      { card: "Gross transaction value", means: "Total successful fan call/room payments in the period.", rule: "transaction_history SUM(total_amount), paid call/room, NaN-guarded." },
      { card: "Creator earnings / Commission / Payouts", means: "The recorded amounts behind the tax estimates.", rule: "earnings SUM(amount) / SUM(commission_amount) / SUM(withdraw_amount), in window." },
    ],
  },
  {
    section: "Refunds",
    rows: [
      { card: "Refunds approved · window", means: "Refunds approved in the period (count + amount).", rule: "refund_requests refund_status='approved', in window." },
      { card: "Pending refunds · live", means: "The refund queue right now — count, amount and oldest age.", rule: "refund_requests refund_status='pending', live (no date filter)." },
      { card: "Refunds queue (age buckets)", means: "Approved refunds awaiting completion, by age (0–7 / 8–14 / 15–30 / 30+ = SLA breach).", rule: "refund_requests status='approved' bucketed by age." },
      { card: "Refund status mix / Approved trend", means: "Split of refunds by status; approved refunds over time.", rule: "refund_requests grouped by status / by period (auto granularity)." },
    ],
  },
  {
    section: "Compliance",
    rows: [
      { card: "Rejected creators", means: "Creator accounts flagged rejected (lifetime count).", rule: "users role=creator, is_rejected=true." },
    ],
  },
];
